/* eslint-disable max-lines */

import { type ClassValue, clsx } from "clsx";
import { differenceInDays, differenceInMonths } from "date-fns";
import Cookies from "js-cookie";
import { Dispatch, SetStateAction } from "react";
import { createSearchParams, Location, NavigateFunction } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import {
    apisUrlsData,
    constantsData,
    filtersFormData,
    formFieldNamesData,
    permissionsData,
    routesUrlsData,
} from "@/data";
import {
    AccountEnum,
    AppEnum,
    ModuleEnum,
    TransactionFiltersKeyEnum,
} from "@/enums";
import { OfflineDataSourceAmountFieldInterface } from "@/interfaces";
import { setCurrentUserInfo, toggleSidebar } from "@/redux/actions";
import { store } from "@/redux/store";
import {
    CurrencyType,
    ModuleType,
    RoleType,
    TransactionAmountType,
    TransactionFiltersKeyType,
} from "@/types";

import { axios } from "./axios";

export const generateValidClassNameHandler = (...inputs: ClassValue[]) => {
    const merged = twMerge(clsx(inputs));

    return merged === "" ? undefined : merged;
};

export const updateStateHandler = (oldState: object, newState: object) => ({
    ...oldState,
    ...newState,
});

export const formatStringHandler = (string?: string): string => (string ?? "").
    replace(
        /_/g,
        " ",
    ).
    split(" ").
    map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase()). // eslint-disable-line
    join(" ").
    replace(
        /\(\s*(mena|latam)\s*\)/gi,
        (_, substring) => `(${substring.toUpperCase()})`,
    );

export const formatNumberHandler = (value: string | number): string => {
    const number = typeof value === "string" ? parseFloat(value) : value;

    if (Number.isNaN(number)) return String(value);

    return new Intl.NumberFormat(
        "en",
        {
            compactDisplay: "short",
            maximumFractionDigits: 2,
            notation: "compact",
        },
    ).format(number);
};

export const logoutHandler = async (
    pathname: string = "",
    navigate: (path: string) => void = () => {}, // eslint-disable-line
    isAdmin? : boolean,
) => {
    const authRoutes = Object.values(routesUrlsData.auth);

    const refreshToken = Cookies.get("refreshToken");

    if (refreshToken) {
        await axios.post(
            apisUrlsData.auth.logout,
            {},
            { isAdmin },
        );
    }

    Cookies.remove("accessToken");

    Cookies.remove("refreshToken");

    localStorage.removeItem("isAdmin");

    if (navigate && !authRoutes.includes(pathname)) navigate(`${routesUrlsData.auth.login}${isAdmin ? `?app=${AppEnum.ADMIN.toLocaleLowerCase()}` : ""}`);

    store.dispatch(setCurrentUserInfo({
        createdTimestamp: new Date(),
        email: "",
        emailVerified: false,
        enabled: false,
        firstName: "",
        id: "",
        isAdmin: false,
        isAuthenticated: false,
        lastName: "",
        role: "" as RoleType,
        username: "",
    }));

    if (store.getState().isSideBarCollapsed) store.dispatch(toggleSidebar());
};

export const debounce = <CallbackFunction extends (...args: any[]) => void>( // eslint-disable-line
    callback: CallbackFunction,
    time: number,
): ((...args: Parameters<CallbackFunction>) => void) => { // eslint-disable-line
    let timeout: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<CallbackFunction>) => {
        if (timeout) clearTimeout(timeout);

        timeout = setTimeout(
            () => {
                callback(...args);
            },
            time,
        );
    };
};

export const buildNavigationQueryParamsHandler = (
    newParams: Record<string, string>,
    location: { search: string },
    withoutMergedParams?: boolean,
) : string => {
    const existingParams = !withoutMergedParams && location?.search ? Object.fromEntries(new URLSearchParams(location.search)) : {};

    const mergedParams: Record<string, string> = {
        ...existingParams,
        ...newParams,
    };

    const filteredParams = Object.fromEntries(Object.entries(mergedParams).filter(([_, value]) => value !== "")); // eslint-disable-line

    const params = createSearchParams(filteredParams).toString();

    return params;
};

export const buildRequestQueryParamsHandler = (queries: Record<string, string | number | boolean | Date | null | undefined>): string => {
    const params = new URLSearchParams();

    Object.entries(queries).forEach(([key, value]) => {
        if (value !== undefined && value !== "" && value !== null) {
            params.append(
                key,
                value.toString(),
            );
        }
    });

    return `?${params.toString()}`;
};

export const extractUrlQueryParamsHandler = (params: { get: (key: string) => string | null}) => { // eslint-disable-line
    const searchParam: string = params.get("search") || "";

    const pageParam: number = parseInt(
        params.get("page") || "1",
        10,
    );

    const limitParam: number = parseInt(
        params.get("limit") || "15",
        10,
    );

    const sortByParam: string = params.get("sortBy") || "";

    const sortNumberParam: number = parseInt(
        params.get("sortNumber") || "0",
        10,
    );
    const sortOrderParam: string = params.get("sortOrder") || "";

    const categoryParam: string = params.get("category") || "";

    const parentCategoryParam: string = params.get("parentCategory") || "";

    const statusParam: string = params.get("status") || "";

    const typeParam: string = params.get("type") || "";

    const emailParam: string = params.get("email") || "";

    const usernameParam: string = params.get("username") || "";

    const currencyParam: string = params.get("currency") || "";

    const connectionTypeParam: string = params.get("connectionType") || "";

    const dateFromParam: string = params.get("dateFrom") || "";

    const dateToParam: string = params.get("dateTo") || "";

    const allowedDaySlipFromParam: string = params.get("allowedDaySlipFrom") || "";

    const allowedDaySlipToParam: string = params.get("allowedDaySlipTo") || "";

    const confidenceFromParam: string = params.get("confidenceFrom") || "";

    const confidenceToParam: string = params.get("confidenceTo") || "";

    const amountFromParam: string = params.get("amountFrom") || "";

    const amountToParam: string = params.get("amountTo") || "";

    const accountsParam: string = params.get("accounts") || "";

    const dataSourcesParam: string = params.get("dataSources") || "";

    const dataSourceParam: string = params.get("dataSource") || "";

    const organizationParam: string = params.get("organization") || "";

    const windowSizeParam: string = params.get("windowSize") || "";

    const ruleParam: string = params.get("rule") || "";

    const subscriptionPlanParam = params.get("subscriptionPlan") || "";

    const searchAParam: string = params.get("searchA") || "";

    const pageAParam: number = parseInt(
        params.get("pageA") || "1",
        10,
    );

    const limitAParam: number = parseInt(
        params.get("limitA") || "15",
        10,
    );

    const statusAParam: string = params.get("statusA") || "";

    const typeAParam: string = params.get("typeA") || "";

    const dateFromAParam: string = params.get("dateFromA") || "";

    const dateToAParam: string = params.get("dateToAParam") || "";

    const amountFromAParam: string = params.get("amountFromA") || "";

    const amountToAParam: string = params.get("amountToA") || "";

    const searchBParam: string = params.get("searchB") || "";

    const pageBParam: number = parseInt(
        params.get("pageB") || "1",
        10,
    );

    const limitBParam: number = parseInt(
        params.get("limitB") || "15",
        10,
    );

    const statusBParam: string = params.get("statusB") || "";

    const typeBParam: string = params.get("typeB") || "";

    const dateFromBParam: string = params.get("dateFromBParam") || "";

    const dateToBParam: string = params.get("dateToBParam") || "";

    const amountFromBParam: string = params.get("amountFromB") || "";

    const amountToBParam: string = params.get("amountToB") || "";

    return {
        accountsParam,
        allowedDaySlipFromParam,
        allowedDaySlipToParam,
        amountFromAParam,
        amountFromBParam,
        amountFromParam,
        amountToAParam,
        amountToBParam,
        amountToParam,
        categoryParam,
        confidenceFromParam,
        confidenceToParam,
        connectionTypeParam,
        currencyParam,
        dataSourceParam,
        dataSourcesParam,
        dateFromAParam,
        dateFromBParam,
        dateFromParam,
        dateToAParam,
        dateToBParam,
        dateToParam,
        emailParam,
        limitAParam,
        limitBParam,
        limitParam,
        organizationParam,
        pageAParam,
        pageBParam,
        pageParam,
        parentCategoryParam,
        ruleParam,
        searchAParam,
        searchBParam,
        searchParam,
        sortByParam,
        sortNumberParam,
        sortOrderParam,
        statusAParam,
        statusBParam,
        statusParam,
        subscriptionPlanParam,
        typeAParam,
        typeBParam,
        typeParam,
        usernameParam,
        windowSizeParam,
    };
};

export const checkHasFilteredValuesHandler = (filters: Record<string, any>): boolean => Object.values(filters).some((value) => { // eslint-disable-line
    if (Array.isArray(value)) return value.length > 0;

    if (value && typeof value === "object" && "from" in value && "to" in value) {
        const fromValid = value.from !== undefined && value.from !== "";

        const toValid = value.to !== undefined && value.to !== "";

        return fromValid || toValid;
    }

    return value !== undefined && value !== "";
});

export const getFilteredValuesHandler = (filters: Record<string, any>): Record<string, any> => { // eslint-disable-line
    const formattedFilteredFilters = {
        ...filters,
        ...filters?.amountFrom && filters?.amountTo && {
            amount: {
                from: filters?.amountFrom,
                to: filters?.amountTo,
            },
            amountFrom: "",
            amountTo: "",
        },
        ...filters?.allowedDaySlipFrom && filters?.allowedDaySlipTo && {
            allowedDaySlip: {
                from: filters?.allowedDaySlipFrom,
                to: filters?.allowedDaySlipTo,
            },
            allowedDaySlipFrom: "",
            allowedDaySlipTo: "",
        },
        ...filters?.confidenceFrom && filters?.confidenceTo && {
            confidence: {
                from: filters?.confidenceFrom,
                to: filters?.confidenceTo,
            },
            confidenceFrom: "",
            confidenceTo: "",
        },
    };

    return Object.fromEntries(
        Object.entries(formattedFilteredFilters).filter(([_, value]) => { // eslint-disable-line
            if (Array.isArray(value)) return value.length > 0;

            if (value && typeof value === "object" && "from" in value && "to" in value) {
                const fromValid = value.from !== undefined && value.from !== "";

                const toValid = value.to !== undefined && value.to !== "";

                return fromValid || toValid;
            }

            return value !== undefined && value !== "";
        }),
    );
};

export const setTableFiltersHandler = <TSetFilters extends Record<string, (value: any) => void>>( // eslint-disable-line
    values: Record<string, any>, // eslint-disable-line
    module: keyof typeof filtersFormData,
    navigate: NavigateFunction,
    location: Location,
    setFilters?: TSetFilters,
    transactionFiltersKey?: TransactionFiltersKeyType,
    filtersKey?: "A" | "B",
    isSideBySideFilters?: boolean,
    withoutFilteringNavigation?: boolean,
) => {
    const {
        accounts,
        action,
        allowedDaySlipFrom,
        allowedDaySlipTo,
        amountFrom,
        amountTo,
        category,
        confidenceFrom,
        confidenceTo,
        connectionType,
        currency,
        dataSource,
        dataSources,
        date,
        organization,
        rule,
        status,
        subscriptionPlan,
        type,
        windowSize,
    } = values;

    if (module === ModuleEnum.ACCOUNT) {
        if (date?.from && date?.to) {
            setFilters?.setDateFrom(new Date(date.from).toISOString());

            setFilters?.setDateTo(new Date(date.to).toISOString());
        } else {
            setFilters?.setDateFrom("");

            setFilters?.setDateTo("");
        }

        if (category) setFilters?.setCategory(category);
        else setFilters?.setCategory("");

        if (status) setFilters?.setStatus(status);
        else setFilters?.setStatus("");

        if (type) setFilters?.setType(type);
        else setFilters?.setType("");

        if (currency) setFilters?.setCurrency(currency);
        else setFilters?.setCurrency("");
    }

    if (module === ModuleEnum.DATA_SOURCE) {
        if (status) setFilters?.setStatus(status);
        else setFilters?.setStatus("");

        if (type) setFilters?.setType(type);
        else setFilters?.setType([]);

        if (currency) setFilters?.setCurrency(currency);
        else setFilters?.setCurrency("");

        if (connectionType) setFilters?.setConnectionType(connectionType);
        else setFilters?.setConnectionType("");
    }

    if (module === ModuleEnum.TRANSACTION) {
        if (date?.from && date?.to) {
            setFilters?.setDateFrom(new Date(date.from).toISOString());

            setFilters?.setDateTo(new Date(date.to).toISOString());
        } else {
            setFilters?.setDateFrom("");

            setFilters?.setDateTo("");
        }

        if (transactionFiltersKey === TransactionFiltersKeyEnum.ACCOUNT) {
            if (dataSource) setFilters?.setDataSource(dataSource);
            else setFilters?.setDataSource("");
        }

        if (transactionFiltersKey !== TransactionFiltersKeyEnum.DATA_SOURCE) {
            if (status) setFilters?.setStatus(status);
            else setFilters?.setStatus([]);
        }

        if (transactionFiltersKey !== TransactionFiltersKeyEnum.LEDGER) {
            if (type) setFilters?.setType(type);
            else setFilters?.setType("");
        }

        if (transactionFiltersKey === TransactionFiltersKeyEnum.LEDGER) {
            if (accounts) setFilters?.setAccounts(accounts);
            else setFilters?.setAccounts([]);

            if (dataSources) setFilters?.setDataSources(dataSources);
            else setFilters?.setDataSources([]);
        }

        if (amountFrom && amountTo) {
            setFilters?.setAmountFrom(amountFrom);

            setFilters?.setAmountTo(amountTo);
        } else {
            setFilters?.setAmountFrom("");

            setFilters?.setAmountTo("");
        }
    }

    if (module === ModuleEnum.TRANSACTION_AUDIT_LOG || module === ModuleEnum.DATA_SOURCE_AUDIT_LOG || module === ModuleEnum.TRANSACTION_VERSION_LOG) {
        if (module === ModuleEnum.TRANSACTION_AUDIT_LOG) {
            if (action) setFilters?.setAction(action);
            else setFilters?.setAction("");
        }

        if (date?.from && date?.to) {
            setFilters?.setDateFrom(new Date(date.from).toISOString());

            setFilters?.setDateTo(new Date(date.to).toISOString());
        } else {
            setFilters?.setDateFrom("");

            setFilters?.setDateTo("");
        }
    }

    if (module === ModuleEnum.ORGANIZATIONS_ENTRIES) {
        if (date?.from && date?.to) {
            setFilters?.setDateFrom(new Date(date.from).toISOString());

            setFilters?.setDateTo(new Date(date.to).toISOString());
        } else {
            setFilters?.setDateFrom("");

            setFilters?.setDateTo("");
        }

        if (windowSize) setFilters?.setWindowSize(windowSize);
        else setFilters?.setWindowSize("");

        if (organization) setFilters?.setOrganization(organization);
        else setFilters?.setOrganization("");
    }

    if (module === ModuleEnum.ORGANIZATION_ENTRIES) {
        if (date?.from && date?.to) {
            setFilters?.setDateFrom(new Date(date.from).toISOString());

            setFilters?.setDateTo(new Date(date.to).toISOString());
        } else {
            setFilters?.setDateFrom("");

            setFilters?.setDateTo("");
        }

        if (windowSize) setFilters?.setWindowSize(windowSize);
        else setFilters?.setWindowSize("");
    }

    if (module === ModuleEnum.ORGANIZATION) {
        if (status) setFilters?.setStatus(status);
        else setFilters?.setStatus("");

        if (subscriptionPlan) setFilters?.setSubscriptionPlan(subscriptionPlan);
        else setFilters?.setSubscriptionPlan("");
    }

    if (module === ModuleEnum.RECONCILIATION) {
        if (status) setFilters?.setStatus(status);
        else setFilters?.setStatus([]);

        if (date?.from && date?.to) {
            setFilters?.setDateFrom(new Date(date.from).toISOString());

            setFilters?.setDateTo(new Date(date.to).toISOString());
        } else {
            setFilters?.setDateFrom("");

            setFilters?.setDateTo("");
        }

        if (dataSources) setFilters?.setDataSources(dataSources);
        else setFilters?.setDataSources([]);
    }

    if (module === ModuleEnum.RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION) {
        if (status) setFilters?.setStatus(status);
        else setFilters?.setStatus("");

        if (rule) setFilters?.setRule(rule);
        else setFilters?.setRule("");

        if (date?.from && date?.to) {
            setFilters?.setDateFrom(new Date(date.from).toISOString());

            setFilters?.setDateTo(new Date(date.to).toISOString());
        } else {
            setFilters?.setDateFrom("");

            setFilters?.setDateTo("");
        }

        if (allowedDaySlipFrom && allowedDaySlipTo) {
            setFilters?.setAllowedDaySlipFrom(allowedDaySlipFrom);
            setFilters?.setAllowedDaySlipTo(allowedDaySlipTo);
        } else {
            setFilters?.setAllowedDaySlipFrom("");
            setFilters?.setAllowedDaySlipTo("");
        }

        if (confidenceFrom && confidenceTo) {
            setFilters?.setConfidenceFrom(confidenceFrom);
            setFilters?.setConfidenceTo(confidenceTo);
        } else {
            setFilters?.setConfidenceFrom("");
            setFilters?.setConfidenceTo("");
        }

        if (amountFrom && amountTo) {
            setFilters?.setAmountFrom(amountFrom);
            setFilters?.setAmountTo(amountTo);
        } else {
            setFilters?.setAmountFrom("");
            setFilters?.setAmountTo("");
        }
    }

    if (!withoutFilteringNavigation) {
        let queryObject = {};

        if (filtersKey === "A" && isSideBySideFilters) {
            queryObject = {
                amountFromA: amountTo && amountFrom ? amountFrom : "",
                amountToA: amountTo && amountFrom ? amountTo : "",
                dateFromA: date?.from ? new Date(date.from).toISOString() : "",
                dateToA: date?.to ? new Date(date.to).toISOString() : "",
                statusA: status ? status.join(",") : "",
                typeA: type || "",
            };
        } else if (filtersKey === "B" && isSideBySideFilters) {
            queryObject = {
                amountFromB: amountTo && amountFrom ? amountFrom : "",
                amountToB: amountTo && amountFrom ? amountTo : "",
                dateFromB: date?.from ? new Date(date.from).toISOString() : "",
                dateToB: date?.to ? new Date(date.to).toISOString() : "",
                statusB: status ? status.join(",") : "",
                typeB: type || "",
            };
        } else {
            queryObject = {
                accounts: accounts ? accounts.join(",") : "",
                action: action || "",
                allowedDaySlipFrom: allowedDaySlipFrom && allowedDaySlipTo ? allowedDaySlipFrom : "",
                allowedDaySlipTo: allowedDaySlipFrom && allowedDaySlipTo ? allowedDaySlipTo : "",
                amountFrom: amountTo && amountFrom ? amountFrom : "",
                amountTo: amountTo && amountFrom ? amountTo : "",
                category: category || "",
                confidenceFrom: confidenceFrom && confidenceTo ? confidenceFrom : "",
                confidenceTo: confidenceFrom && confidenceTo ? confidenceTo : "",
                connectionType: connectionType || "",
                currency: currency || "",
                dataSource: dataSource || "",
                dataSources: dataSources ? dataSources.join(",") : "",
                dateFrom: date?.from ? new Date(date.from).toISOString() : "",
                dateTo: date?.to ? new Date(date.to).toISOString() : "",
                organization: organization || "",
                rule: rule || "",
                subscriptionPlan: subscriptionPlan || "",
                type: type ? module === ModuleEnum.DATA_SOURCE ? type.join(",") : type : "", // eslint-disable-line
                status: status ? module === ModuleEnum.ACCOUNT || module === ModuleEnum.DATA_SOURCE || module === ModuleEnum.ORGANIZATION || module === ModuleEnum.RECONCILIATION_AUDIT_LOG_MATCHING_TRANSACTION ? status : status.join(",") : "", // eslint-disable-line
                windowSize: windowSize || "",
            };
        }

        navigate(
            {
                pathname: window.location.pathname,
                search: buildNavigationQueryParamsHandler(
                    queryObject,
                    location,
                ),
            },
            {
                replace: true,
                state: { from: location.state?.from },
            },
        );
    }
};

export const downloadFileHandler = (filePath : string, fileName : string) => {
    const link = document.createElement("a");

    link.href = filePath;

    link.download = fileName;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
};

export const getErrorMessageHandler = (error : unknown, defaultMessage: string = constantsData.msgs.unknownError) => (error && typeof error === "object" && (error as { response: { data: { error: string } } }).response?.data?.error ? (error as { response: { data: { error: string } } }).response?.data?.error : defaultMessage);

export const getCurrencySymbolHandler = (currency : CurrencyType, locale = "en-US") => (currency ? (0).toLocaleString(
    locale,
    {
        currency,
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
        style: "currency",
    },
).replace(
    /\d/g,
    "",
).trim() : "");

export const formatCurrencyAmountHandler = (amount: number, currency: string) => new Intl.NumberFormat(
    "en-US",
    {
        currency,
        minimumFractionDigits: 2,
        style: "currency",
    },
).format(Math.abs(amount));

export const getFileExtensionHandler = (filename: string) => filename.split(".").pop()?.toLowerCase() || "";

export const canDoHandler = (role: RoleType, module: ModuleType, action: keyof typeof permissionsData.actions) => {
    const permissions = (permissionsData.data[role] as Record<string, Record<string, boolean>>)?.[module];

    return !!permissions?.[action];
};

export const normalizeAuthHandler = (value: string) => {
    if (!value) return undefined;

    let token = String(value).trim();

    token = token.replace(
        /^['"]|['"]$/g,
        "",
    );

    token = token.replace(
        /\r?\n/g,
        "",
    );

    const bearer = token.match(/^bearer\s+(.+)$/i);

    if (bearer) return `Bearer ${bearer[1].trim()}`;

    if (/^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/.test(token)) return `Bearer ${token}`;

    return token;
};

export const calculateDateDifferenceHandler = (dateFrom: string, dateTo: string): string => {
    if (!dateFrom || !dateTo) return "0 days";

    const startDate = new Date(dateFrom);
    const endDate = new Date(dateTo);

    const monthDifference = differenceInMonths(
        endDate,
        startDate,
    );

    // If the difference is 1 or more full months, return the month count
    if (monthDifference > 0) return `${monthDifference} ${monthDifference === 1 ? "month" : "months"}`;

    const dayDifference = differenceInDays(
        endDate,
        startDate,
    );

    return `${dayDifference} ${dayDifference === 1 ? "day" : "days"}`;
};

export const getTwoCodependentFormFieldsSelectOptionsHandler = <Options extends readonly (string | { id: string, name: string, })[]>(
    name: keyof typeof formFieldNamesData,
    firstField: {
        name: keyof typeof formFieldNamesData;
        value: string | undefined;
    },
    secondField: {
        name: keyof typeof formFieldNamesData;
        value: string | undefined;
    },
    options: Options,
    extraDisabled?: (
        option: Options[number], // eslint-disable-line
        otherValue?: string, // eslint-disable-line
    ) => boolean,
) => {
    let selectOptions: string[] | {
        disabled?: boolean,
        id: Options[number],
        name: Options[number],
    }[] = [];

    const otherValue = name === firstField.name ? secondField.value : firstField.value;

    selectOptions = options.map((option) => {
        const id = typeof option === "string" ? option : option.id;

        const label = typeof option === "string" ? option : option.name;

        return {
            disabled: (!!otherValue && id === otherValue) || (extraDisabled ? !!extraDisabled(
                option,
                otherValue,
            ) : false),
            id,
            name: label,
        };
    });

    return selectOptions;
};

export const getTwoCodependentFormFieldsMultiSelectOptionsHandler = <Array extends readonly string[]>(
    oppositeField: {
        name: keyof typeof formFieldNamesData;
        value: Array[number][] | undefined | string[];
    },
    options: Array,
) => {
    let multiSelectOptions: string[] | {
        disabled?: boolean,
        id: Array[number],
        name: Array[number],
    }[] = [];

    const oppositeFieldSelectedValues = oppositeField.value ?? [];

    multiSelectOptions = options.map((option) => ({
        disabled: oppositeFieldSelectedValues.includes(option),
        id: option,
        name: option,
    }));

    return multiSelectOptions;
};

export const getAmountFieldAccountTypeHandler = (account: { credit: string; debit: string }) => (account.credit ? AccountEnum.CREDIT : account.debit ? AccountEnum.DEBIT : null as AccountEnum.CREDIT | AccountEnum.DEBIT | null); // eslint-disable-line

export const getAmountFieldsWithAccountsConfigsHandler = (
    selectedAccounts: {
        fee: {
            credit: string,
            debit: string,
        },
        gross: {
            credit: string,
            debit: string,
        },
        net: {
            credit: string,
            debit: string,
        }
    },
    selectedAmounts?: Record<TransactionAmountType, {
        checked: boolean,
        disabled?: boolean,
    }> | null,
    amountMappingFields?: {
        data: OfflineDataSourceAmountFieldInterface | null,
        type: TransactionAmountType,
    }[] | null,
    hasMapping?: boolean,
) => {
    let isAllAmountFieldsMappedOrSelected: boolean;

    if (hasMapping) isAllAmountFieldsMappedOrSelected = amountMappingFields?.every(({ data: fieldData }) => !!fieldData?.fieldName) || (amountMappingFields?.filter(({ data: fieldData }) => !!fieldData?.fieldName).length === 2 && amountMappingFields.filter(({ data: fieldData }) => !!fieldData?.calculation?.condition).length === 1);
    else isAllAmountFieldsMappedOrSelected = selectedAmounts ? Object.values(selectedAmounts).every(({ checked }) => checked) : false;

    const netAccountType = getAmountFieldAccountTypeHandler(selectedAccounts.net);

    const grossAccountType = getAmountFieldAccountTypeHandler(selectedAccounts.gross);

    const feeAccountType = getAmountFieldAccountTypeHandler(selectedAccounts.fee);

    let enforcedFeeNetAccountType: AccountEnum.CREDIT | AccountEnum.DEBIT | null = null;

    let enforcedGrossAccountType: AccountEnum.CREDIT | AccountEnum.DEBIT | null = null;

    if (isAllAmountFieldsMappedOrSelected) {
        if (grossAccountType) {
            enforcedGrossAccountType = grossAccountType;

            enforcedFeeNetAccountType = grossAccountType === AccountEnum.CREDIT ? AccountEnum.DEBIT : AccountEnum.CREDIT;
        } else if (feeAccountType || netAccountType) {
            const currentFeeNetAccountType = feeAccountType ?? netAccountType;

            enforcedFeeNetAccountType = currentFeeNetAccountType!;

            enforcedGrossAccountType = currentFeeNetAccountType === AccountEnum.CREDIT ? AccountEnum.DEBIT : AccountEnum.CREDIT;
        }
    }

    let firstMappedOrSelectedAmountField: {
        data?: OfflineDataSourceAmountFieldInterface | null,
        type: TransactionAmountType,
    } | null;

    if (hasMapping) firstMappedOrSelectedAmountField = amountMappingFields?.filter(({ data: fieldData }) => fieldData?.fieldName)?.[0] || null;
    else {
        firstMappedOrSelectedAmountField = selectedAmounts ? Object.entries(selectedAmounts).reduce<{ type: TransactionAmountType } | null>(
            (currentAmount, [type, { checked }]) => currentAmount || (checked ? { type: type as TransactionAmountType } : null),
            null,
        ) : null;
    }

    return {
        enforcedFeeNetAccountType,
        enforcedGrossAccountType,
        firstMappedOrSelectedAmountField,
        isAllAmountFieldsMappedOrSelected,
    };
};

export const checkAmountFieldsValidationWithAccountsHandler = ( // eslint-disable-line
    amountFieldsMappedOrSelected: {
        amountType: TransactionAmountType;
        creditAccount: string | null;
        debitAccount: string | null;
    }[],
    setInValidAccounts: Dispatch<SetStateAction<Partial<Record<TransactionAmountType, {
        credit: boolean,
        debit: boolean,
    }>>>>,
    isBankDataSourceType: boolean,
    storedAmountFields?: OfflineDataSourceAmountFieldInterface[],
    hasMapping?: boolean,
) : boolean => {
    const firstAmountFieldMappedOrSelected = amountFieldsMappedOrSelected[0];

    const secondAmountFieldMappedOrSelected = amountFieldsMappedOrSelected[1];

    const thirdAmountFieldMappedOrSelected = amountFieldsMappedOrSelected[2];

    const fieldHasEitherAccounts = (field: typeof amountFieldsMappedOrSelected[number]) => Boolean(field?.creditAccount) || Boolean(field?.debitAccount);

    const fieldHasBothAccounts = (field: typeof amountFieldsMappedOrSelected[number]) => Boolean(field?.creditAccount) && Boolean(field?.debitAccount);

    if (amountFieldsMappedOrSelected.length === 3) {
        setInValidAccounts({
            [firstAmountFieldMappedOrSelected?.amountType]: {
                credit: !firstAmountFieldMappedOrSelected?.creditAccount && !firstAmountFieldMappedOrSelected?.debitAccount,
                debit: !firstAmountFieldMappedOrSelected?.creditAccount && !firstAmountFieldMappedOrSelected?.debitAccount,
            },
            [secondAmountFieldMappedOrSelected?.amountType]: {
                credit: !secondAmountFieldMappedOrSelected?.creditAccount && !secondAmountFieldMappedOrSelected?.debitAccount,
                debit: !secondAmountFieldMappedOrSelected?.creditAccount && !secondAmountFieldMappedOrSelected?.debitAccount,
            },
            [thirdAmountFieldMappedOrSelected?.amountType]: {
                credit: !thirdAmountFieldMappedOrSelected?.creditAccount && !thirdAmountFieldMappedOrSelected?.debitAccount,
                debit: !thirdAmountFieldMappedOrSelected?.creditAccount && !thirdAmountFieldMappedOrSelected?.debitAccount,
            },
        });

        return amountFieldsMappedOrSelected.every(fieldHasEitherAccounts);
    }

    if (amountFieldsMappedOrSelected.length === 2) {
        if (hasMapping) {
            const remainingFieldWithoutTag = storedAmountFields?.find(({ fieldName }) => !fieldName);

            const remainingFieldWithoutTagCalculated = remainingFieldWithoutTag?.calculation?.condition;

            if (remainingFieldWithoutTagCalculated) {
                setInValidAccounts({
                    [firstAmountFieldMappedOrSelected?.amountType]: {
                        credit: !firstAmountFieldMappedOrSelected?.creditAccount && !firstAmountFieldMappedOrSelected?.debitAccount,
                        debit: !firstAmountFieldMappedOrSelected?.creditAccount && !firstAmountFieldMappedOrSelected?.debitAccount,
                    },
                    [secondAmountFieldMappedOrSelected?.amountType]: {
                        credit: !secondAmountFieldMappedOrSelected?.creditAccount && !secondAmountFieldMappedOrSelected?.debitAccount,
                        debit: !secondAmountFieldMappedOrSelected?.creditAccount && !secondAmountFieldMappedOrSelected?.debitAccount,
                    },
                    [remainingFieldWithoutTag?.amountType]: {
                        credit: !remainingFieldWithoutTag?.creditAccount && !remainingFieldWithoutTag?.debitAccount,
                        debit: !remainingFieldWithoutTag?.creditAccount && !remainingFieldWithoutTag?.debitAccount,
                    },
                });

                return fieldHasEitherAccounts(remainingFieldWithoutTag) && amountFieldsMappedOrSelected.every(fieldHasEitherAccounts);
            }
        }

        setInValidAccounts({
            [firstAmountFieldMappedOrSelected?.amountType]: {
                credit: !firstAmountFieldMappedOrSelected?.creditAccount,
                debit: !firstAmountFieldMappedOrSelected?.debitAccount,
            },
            [secondAmountFieldMappedOrSelected?.amountType]: {
                credit: !secondAmountFieldMappedOrSelected?.creditAccount,
                debit: !secondAmountFieldMappedOrSelected?.debitAccount,
            },
        });

        return amountFieldsMappedOrSelected.every(fieldHasBothAccounts);
    }

    if (isBankDataSourceType) {
        setInValidAccounts({
            [firstAmountFieldMappedOrSelected?.amountType]: {
                credit: !firstAmountFieldMappedOrSelected?.creditAccount && !firstAmountFieldMappedOrSelected?.debitAccount,
                debit: !firstAmountFieldMappedOrSelected?.creditAccount && !firstAmountFieldMappedOrSelected?.debitAccount,
            },
        });

        return amountFieldsMappedOrSelected.some(fieldHasEitherAccounts);
    }

    setInValidAccounts({
        [firstAmountFieldMappedOrSelected?.amountType]: {
            credit: !firstAmountFieldMappedOrSelected?.creditAccount,
            debit: !firstAmountFieldMappedOrSelected?.debitAccount,
        },
    });

    return fieldHasBothAccounts(firstAmountFieldMappedOrSelected);
};

export const formatConfidenceScoreHandler = (score: number): string => {
    const percentage = score * 100;

    return `${percentage % 1 === 0 ? percentage : percentage.toFixed(2)}%`;
};

export const returnFloatOrUndefinedHandler = (value: string): number | undefined => {
    const parsed = parseFloat(value);

    return Number.isNaN(parsed) ? undefined : parsed;
};
